import type { AgentCommand } from '../fc-session.service';
import { isFcV5WireEvent } from './fc-v5.types';

type MutableAgentCommand = AgentCommand & Record<string, unknown>;

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asRole(value: unknown): 'agent' | 'user' | undefined {
  return value === 'agent' || value === 'user' ? value : undefined;
}

/**
 * Normaliza eventos wire fc-v5 a la forma interna que consume el componente.
 * Para `next_question` v5 el texto y area salen de `question_text` y
 * `knowledge_name`; se mantiene fallback explícito a campos históricos solo
 * para runtimes viejos durante rollout.
 */
export function adaptFcV5Command(payload: unknown): AgentCommand {
  if (!payload || typeof payload !== 'object') {
    return payload as AgentCommand;
  }

  if (!isFcV5WireEvent(payload)) {
    return payload as AgentCommand;
  }

  const raw = payload as Record<string, unknown>;
  const adapted: MutableAgentCommand = { ...(payload as MutableAgentCommand) };

  if (typeof raw['assistant_turn_id'] === 'string') {
    adapted['assistantTurnId'] = raw['assistant_turn_id'];
  }

  switch (raw['type']) {
    case 'transcription': {
      adapted['role'] = asRole(raw['role']) ?? 'user';
      adapted['text'] = asString(raw['text']) ?? asString(raw['transcript']);
      break;
    }

    case 'session_ack': {
      const state = raw['state'] && typeof raw['state'] === 'object'
        ? raw['state'] as Record<string, unknown>
        : undefined;
      if (state) {
        adapted['mic_open'] = state['mic_open'] === true;
        adapted['phase'] = asString(state['phase']) ?? adapted['phase'];
        adapted['questionIndex'] = asNumber(state['current_question_index']);
        adapted['question_idx'] = adapted['questionIndex'];
        adapted['question_id'] = asString(state['current_question_id']);
      }
      break;
    }

    case 'assistant_turn_started':
      adapted['questionIndex'] = asNumber(raw['question_index']) ?? asNumber(raw['question_idx']);
      adapted['question_idx'] = adapted['questionIndex'];
      adapted['display_text'] = asString(raw['display_text']);
      adapted['display_revision'] = asString(raw['display_revision']);
      adapted['turn_kind'] = asString(raw['turn_kind']);
      adapted['text'] = asString(raw['display_text']) ?? asString(raw['text']);
      adapted['areaName'] = asString(raw['knowledge_name']) ?? asString(raw['knowledge_id']);
      break;
    case 'assistant_logical_complete':
    case 'mic_open':
    case 'user_turn_complete': {
      adapted['questionIndex'] = asNumber(raw['question_index']) ?? asNumber(raw['question_idx']);
      adapted['question_idx'] = adapted['questionIndex'];
      break;
    }

    case 'next_question': {
      adapted['questionIndex'] = asNumber(raw['question_index']) ?? asNumber(raw['question_idx']);
      adapted['question_idx'] = adapted['questionIndex'];
      adapted['text'] = asString(raw['question_text']);
      adapted['areaName'] = asString(raw['knowledge_name']) ?? asString(raw['knowledge_id']);
      adapted['total_questions'] = asNumber(raw['total_questions']);
      break;
    }

    case 'state_resync': {
      const snapshot = raw['snapshot'] && typeof raw['snapshot'] === 'object'
        ? raw['snapshot'] as Record<string, unknown>
        : undefined;
      const currentQuestion = snapshot?.['current_question'] && typeof snapshot['current_question'] === 'object'
        ? snapshot['current_question'] as Record<string, unknown>
        : undefined;
      const assistantTurn = snapshot?.['assistant_turn'] && typeof snapshot['assistant_turn'] === 'object'
        ? snapshot['assistant_turn'] as Record<string, unknown>
        : undefined;
      const progress = snapshot?.['progress'] && typeof snapshot['progress'] === 'object'
        ? snapshot['progress'] as Record<string, unknown>
        : undefined;

      adapted['phase'] = asString(snapshot?.['phase']) ?? adapted['phase'];
      adapted['mic_open'] = snapshot?.['mic_open'] === true;
      adapted['question_id'] = asString(currentQuestion?.['question_id']);
      adapted['questionIndex'] = asNumber(currentQuestion?.['question_index']);
      adapted['question_idx'] = adapted['questionIndex'];
      adapted['text'] = asString(currentQuestion?.['question_text']);
      adapted['areaName'] = asString(currentQuestion?.['knowledge_name']);
      adapted['knowledge_id'] = asString(currentQuestion?.['knowledge_id']);
      adapted['assistant_turn_id'] = asString(assistantTurn?.['assistant_turn_id']);
      adapted['assistantTurnId'] = adapted['assistant_turn_id'];
      adapted['audio_seq_end'] = asNumber(assistantTurn?.['audio_seq_end']);
      adapted['total_questions'] = asNumber(progress?.['total_questions']);
      adapted['elapsed_seconds'] = asNumber(snapshot?.['elapsed_seconds']);
      break;
    }
  }

  return adapted as AgentCommand;
}
